import { useEffect, useState } from 'react'
import { Check, FileText, Trash2 } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { Button, Card } from '../ui/kit'
import { STATUTORY_FALLBACK, TERMS_PRESETS } from '../lib/cancellationTerms'

/**
 * Stornobedingungen des Betriebs — ausdrücklich freiwillig.
 *
 * WARUM KEIN PFLICHTFELD, KEIN WARNZEICHEN, KEIN ROTER PUNKT: wer nichts vereinbart,
 * hat trotzdem Rechte. Ohne gesonderte Abrede gelten die gesetzlichen Bestimmungen,
 * und die geben dem Vermieter den vereinbarten Preis abzüglich ersparter Aufwendungen
 * und dessen, was eine Nachvermietung einbringt. Eine Karte, die das leere Feld als
 * Mangel darstellt, würde jemandem ein Problem einreden, das er nicht hat.
 *
 * WAS DIE STAFFEL TATSÄCHLICH BRINGT — und der Grund, warum die Karte überhaupt
 * existiert: die gesetzliche Regel verlangt im Streitfall, dass der Vermieter seinen
 * Schaden rechnet und belegt. Eine vereinbarte Staffel nimmt ihm das ab. Das ist der
 * ganze Unterschied, und genau so steht er auch auf der Karte — nicht als Drohung,
 * sondern als Angebot.
 *
 * WARUM DER TEXT BEARBEITBAR BLEIBT: er gehört dem Vermieter. Es sind seine
 * Vertragsbedingungen, er verantwortet sie, und StaySuite berät ihn nicht — es legt
 * ihm einen Entwurf hin. Der Hinweis darauf steht auf der Karte und nicht im
 * Kleingedruckten, weil er die Rollen klärt.
 */
export default function CancellationTermsCard() {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [text, setText] = useState(store.db.tenant.cancellation_terms ?? '')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setText(store.db.tenant.cancellation_terms ?? '')
  }, [store.db.tenant])

  const stored = store.db.tenant.cancellation_terms ?? ''
  const dirty = text.trim() !== stored.trim()

  const save = async () => {
    // Leer heißt leer, nicht Leerzeichen: sonst stünde auf dem Angebot ein weißer
    // Absatz statt des Verweises auf die gesetzlichen Bestimmungen.
    await store.saveTenant({ cancellation_terms: text.trim() || null })
    setSaved(true)
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium flex items-center gap-2">
        <FileText size={16} />
        {de ? 'Stornobedingungen' : 'Cancellation terms'}
      </h2>

      <p className="text-sm text-muted">
        {de
          ? 'Freiwillig. Ohne eigene Bedingungen gelten die gesetzlichen Bestimmungen — Sie bekommen den vereinbarten Preis abzüglich ersparter Aufwendungen und dessen, was eine Nachvermietung einbringt. Eine vereinbarte Staffel nimmt Ihnen im Streitfall ab, diesen Betrag ausrechnen und belegen zu müssen.'
          : 'Optional. Without your own terms the statutory rules apply — you are owed the agreed price less expenses saved and any income from re-letting. An agreed ladder saves you from having to calculate and prove that amount if it ever comes to a dispute.'}
      </p>

      {/* Die Vorschläge stehen über dem Feld, nicht darunter: wer noch nichts hat,
          soll nicht erst an einem leeren Kasten vorbeiscrollen. */}
      <div className="space-y-1.5">
        <div className="text-xs text-muted">
          {de ? 'Vorschlag übernehmen und anpassen:' : 'Take a suggestion and edit it:'}
        </div>
        <div className="grid gap-1.5 sm:grid-cols-3">
          {TERMS_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => { setText(p.text[de ? 'de' : 'en']); setSaved(false) }}
              className="text-left rounded-xl bg-surface-2 px-3 py-2 hover:bg-border
                         min-w-0"
            >
              <div className="text-sm font-medium">{p.label[de ? 'de' : 'en']}</div>
              <div className="text-xs text-muted">{p.hint[de ? 'de' : 'en']}</div>
            </button>
          ))}
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setSaved(false) }}
        rows={text ? 12 : 4}
        placeholder={de
          ? 'Leer lassen — dann gelten die gesetzlichen Bestimmungen.'
          : 'Leave empty — the statutory rules then apply.'}
        className="w-full min-w-0 text-sm bg-surface-2 rounded-xl px-3 py-2.5
                   font-normal leading-relaxed"
      />

      <p className="text-xs text-muted">
        {de
          ? 'Was hier steht, erscheint auf jedem neuen Angebot und auf der Seite, auf der der Gast es annimmt. Bereits verschickte Angebote tragen den Wortlaut, der bei ihrer Erstellung galt — eine spätere Änderung wirkt nicht rückwirkend.'
          : 'What you enter here appears on every new quote and on the page where the guest accepts it. Quotes already sent keep the wording that applied when they were created — a later change does not apply retroactively.'}
      </p>

      {/* Die Rollenklärung. Der Text ist ein Entwurf, keine Rechtsberatung, und die
          Bedingungen sind die des Vermieters — er schließt den Vertrag, nicht wir. */}
      <p className="text-xs text-muted">
        {de
          ? 'Die Vorschläge sind Entwürfe zur eigenen Verwendung und keine Rechtsberatung. Es sind Ihre Vertragsbedingungen; prüfen Sie sie, bevor Sie sie verwenden.'
          : 'The suggestions are drafts for your own use and not legal advice. These are your contract terms; check them before you use them.'}
      </p>

      <div className="flex items-center gap-2">
        <Button variant="primary" onClick={save} disabled={!dirty}>
          {saved && !dirty ? <Check size={16} /> : null}
          {de ? 'Speichern' : 'Save'}
        </Button>
        {text && (
          <Button variant="ghost" onClick={() => { setText(''); setSaved(false) }}>
            <Trash2 size={16} />
            {de ? 'Keine eigenen Bedingungen' : 'No own terms'}
          </Button>
        )}
      </div>

      {!text.trim() && (
        <p className="text-xs bg-surface-2 rounded-xl px-3 py-2">
          {de ? 'Der Gast liest dann: ' : 'The guest then reads: '}
          <span className="italic">{de ? STATUTORY_FALLBACK.de : STATUTORY_FALLBACK.en}</span>
        </p>
      )}
    </Card>
  )
}
